import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RecipeInputsEditor } from './RecipeInputsEditor';
import { RecipeOutputsEditor } from './RecipeOutputsEditor';
import { RECIPE_TYPE_VALUES, RECIPE_TYPE_LABELS } from '../constants';

interface RecipeFormData {
  recipeType: string;
  name: string;
  description: string;
  level: number;
  requiresLearned: boolean;
  inputs: Array<{ itemName: string; quantity: number }>;
  outputs: Array<{ itemName: string; quantity: number; probability?: number }>;
  constraints: Record<string, unknown>;
  isEnabled: boolean;
  metadata: Record<string, unknown>;
}

interface RecipeFormProps {
  initialData?: Partial<RecipeFormData>;
  onSubmit: (data: RecipeFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

export function RecipeForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
}: RecipeFormProps) {
  const [formData, setFormData] = useState<RecipeFormData>({
    recipeType: initialData?.recipeType ?? 'litland',
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    level: initialData?.level ?? 0,
    requiresLearned: initialData?.requiresLearned ?? false,
    inputs: initialData?.inputs ?? [],
    outputs: initialData?.outputs ?? [],
    constraints: initialData?.constraints ?? {},
    isEnabled: initialData?.isEnabled ?? true,
    metadata: initialData?.metadata ?? {},
  });
  const [constraintsText, setConstraintsText] = useState(
    JSON.stringify(initialData?.constraints ?? {}, null, 2),
  );
  const [metadataText, setMetadataText] = useState(
    JSON.stringify(initialData?.metadata ?? {}, null, 2),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    let constraints: Record<string, unknown>;
    let metadata: Record<string, unknown>;
    try {
      constraints = JSON.parse(constraintsText);
    } catch {
      setError('Invalid JSON in constraints field');
      return;
    }
    try {
      metadata = JSON.parse(metadataText);
    } catch {
      setError('Invalid JSON in metadata field');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ ...formData, constraints, metadata });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save recipe');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="recipeType">Recipe Type</Label>
          <Select
            value={formData.recipeType}
            onValueChange={(v) => setFormData((d) => ({ ...d, recipeType: v }))}
          >
            <SelectTrigger id="recipeType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECIPE_TYPE_VALUES.map((t) => (
                <SelectItem key={t} value={t}>
                  {RECIPE_TYPE_LABELS[t] ?? t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData((d) => ({ ...d, description: e.target.value }))}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="level">Level</Label>
          <Input
            id="level"
            type="number"
            value={formData.level}
            onChange={(e) =>
              setFormData((d) => ({ ...d, level: parseInt(e.target.value, 10) || 0 }))
            }
            min={0}
          />
        </div>

        <div className="flex items-center gap-2 pt-6">
          <Checkbox
            id="requiresLearned"
            checked={formData.requiresLearned}
            onCheckedChange={(v: boolean) => setFormData((d) => ({ ...d, requiresLearned: v }))}
          />
          <Label htmlFor="requiresLearned">Requires Learned</Label>
        </div>

        <div className="flex items-center gap-2 pt-6">
          <Checkbox
            id="isEnabled"
            checked={formData.isEnabled}
            onCheckedChange={(v: boolean) => setFormData((d) => ({ ...d, isEnabled: v }))}
          />
          <Label htmlFor="isEnabled">Enabled</Label>
        </div>
      </div>

      <RecipeInputsEditor
        inputs={formData.inputs}
        onChange={(inputs) => setFormData((d) => ({ ...d, inputs }))}
      />

      <RecipeOutputsEditor
        outputs={formData.outputs}
        onChange={(outputs) => setFormData((d) => ({ ...d, outputs }))}
      />

      <div className="space-y-2">
        <Label htmlFor="constraints">Constraints (JSON)</Label>
        <Textarea
          id="constraints"
          value={constraintsText}
          onChange={(e) => setConstraintsText(e.target.value)}
          rows={4}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="metadata">Metadata (JSON)</Label>
        <Textarea
          id="metadata"
          value={metadataText}
          onChange={(e) => setMetadataText(e.target.value)}
          rows={4}
          className="font-mono text-sm"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
